import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Outlet, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Navigate, catchAll, useTypedParams } from '@sorare/routing';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { Box as BoxIcon } from '@sorare/core/src/atoms/icons/Box';
import { BottomPortal } from '@sorare/core/src/atoms/layout/BottomPortal';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  ANY_SPORT_MY_CARDS,
  ANY_SPORT_MY_CARDS_BOXES,
  ANY_SPORT_MY_CARDS_BOXES_TYPE,
  ANY_SPORT_MY_CARDS_CARDS,
  ANY_SPORT_MY_CARDS_CARDS_TYPE,
  ANY_SPORT_MY_CARDS_COLLECTIONS,
  ANY_SPORT_MY_CARDS_COLLECTIONS_TYPE,
  ANY_SPORT_MY_CARDS_ESSENCE,
  ANY_SPORT_MY_CARDS_ESSENCE_TYPE,
  ANY_SPORT_MY_CARDS_VAULT,
} from '@sorare/core/src/constants/__generated__/routes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useQueryString } from '@sorare/core/src/hooks/useQueryString';
import { fantasy, galleryTabs, navLabels } from '@sorare/core/src/lib/glossary';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import { RequireAuth } from '@sorare/core/src/routing/RequireAuth';

import { BoxRaritySwitcher } from 'components/myCards/BoxRaritySwitcher';
import { CardOrCollectionRaritySwitcher } from 'components/myCards/CardOrCollectionRaritySwitcher';
import { EssenceRaritySwitcher } from 'components/myCards/EssenceRaritySwitcher';
import { NewRewardsBanner } from 'components/rewards/NewRewardsBanner';
import { LazyLastFixtureStory } from 'components/so5/LastFixtureStory';

import { MyCardsLayoutContext } from './MyCardsLayoutContext';
import type {
  MyCardsLayoutQuery,
  MyCardsLayoutQueryVariables,
  UnopenedRewardBoxes_CurrentUser,
} from './__generated__/index.graphql';

const RewardsBannerWrapper = styled.div`
  padding: var(--double-unit);
`;

const MaxWidthHorizontal = styled(Horizontal)`
  max-width: 100%;
`;

type Props = {
  currentUser: MyCardsLayoutQuery['currentUser'];
  type: Nullable<
    Exclude<Rarity, typeof Rarity.custom_series> | 'rivals' | 'loyalty'
  >;
};
const MyCardsLayoutComponent = ({ currentUser, type }: Props) => {
  const { generateSportContextPath, matchAnySportPath, sport } =
    useSportContext();
  const isLaptopAndAbove = useIsLaptop();
  const location = useLocation();
  const { lifecycle, update } = useLifecycle();
  const rewardsBannerRef = useRef<HTMLDivElement>(null);
  const { activeRevealableCardCampaigns } = useConfigContext();

  const { currentUser: contextCurrentUser } = useCurrentUserContext();
  const {
    unclaimedSo5Rewards = [],
    unopenedRewardBoxCommon,
    unopenedRewardBoxLimited,
    unopenedRewardBoxRare,
    unopenedRewardBoxSuperRare,
    unopenedRewardBoxUnique,
    unopenedRewardBoxRivals,
    unopenedRewardBoxLoyalty,
    cardShardsChests,
  } = currentUser || {};
  const { cardCounts } = contextCurrentUser || {};

  const unopedBoxCount =
    (unopenedRewardBoxCommon?.totalCount || 0) +
    (unopenedRewardBoxLimited?.totalCount || 0) +
    (unopenedRewardBoxRare?.totalCount || 0) +
    (unopenedRewardBoxSuperRare?.totalCount || 0) +
    (unopenedRewardBoxUnique?.totalCount || 0) +
    (unopenedRewardBoxRivals?.totalCount || 0);

  const cardsCountByRarity: Record<
    Exclude<Rarity, typeof Rarity.custom_series>,
    number
  > = {
    [Rarity.common]: cardCounts?.common || 0,
    [Rarity.limited]: cardCounts?.limited || 0,
    [Rarity.rare]: cardCounts?.rare || 0,
    [Rarity.super_rare]: cardCounts?.superRare || 0,
    [Rarity.unique]: cardCounts?.unique || 0,
  };

  const blockchainRarities = [
    Rarity.limited,
    Rarity.rare,
    Rarity.super_rare,
    Rarity.unique,
  ];

  const blockchainRaritiesWithCommon = [
    Rarity.common,
    Rarity.limited,
    Rarity.rare,
    Rarity.super_rare,
    Rarity.unique,
  ].filter(
    rarityType => rarityType !== Rarity.common || sport === Sport.FOOTBALL
  );

  const boxTypes = [
    ...(unopenedRewardBoxCommon?.totalCount
      ? blockchainRaritiesWithCommon
      : blockchainRarities),
    ...(sport === Sport.FOOTBALL && unopenedRewardBoxRivals?.totalCount
      ? (['rivals'] as const)
      : []),
    ...(sport === Sport.FOOTBALL && unopenedRewardBoxLoyalty?.totalCount
      ? (['loyalty'] as const)
      : []),
  ] as const;

  const unopenedProbabilisticBundlesByRarity: Record<
    (typeof boxTypes)[number],
    number
  > = {
    common: unopenedRewardBoxCommon?.totalCount ?? 0,
    limited: unopenedRewardBoxLimited?.totalCount ?? 0,
    rare: unopenedRewardBoxRare?.totalCount ?? 0,
    super_rare: unopenedRewardBoxSuperRare?.totalCount ?? 0,
    unique: unopenedRewardBoxUnique?.totalCount ?? 0,
    rivals: unopenedRewardBoxRivals?.totalCount ?? 0,
    loyalty: unopenedRewardBoxLoyalty?.totalCount ?? 0,
  };

  const hasCardsOfCurrentRarity =
    !!type &&
    type !== 'rivals' &&
    type !== 'loyalty' &&
    cardsCountByRarity[type] > 0;
  const defaultCardRarity = hasCardsOfCurrentRarity
    ? type
    : Object.entries(cardsCountByRarity).find(([, number]) => !!number)?.[0] ||
      Rarity.limited;

  const hasBoxesOfCurrentRarity =
    !!type && unopenedProbabilisticBundlesByRarity[type] > 0;
  const defaultBoxRarity = hasBoxesOfCurrentRarity
    ? type
    : Object.entries(unopenedProbabilisticBundlesByRarity).find(
        ([, number]) => !!number
      )?.[0] || Rarity.limited;

  const essenceOfCurrentRarity = cardShardsChests?.find(
    chest => chest.rarity === type
  );
  const hasEssenceOfCurrentRarity =
    !!type && !!essenceOfCurrentRarity?.cardShardsCount;
  const defaultEssenceRarity = hasEssenceOfCurrentRarity
    ? type
    : cardShardsChests?.find(chest => chest.cardShardsCount > 0)?.rarity ||
      Rarity.limited;

  const showEssenceTab =
    sport === Sport.FOOTBALL || sport === Sport.NBA || sport === Sport.BASEBALL;

  const revealableCardCampaigns = activeRevealableCardCampaigns ?? [];

  const cardEdition = useQueryString('cardEdition');
  const [displayedCardEdition, setDisplayedCardEdition] = useState<
    string | undefined
  >(cardEdition);

  if (
    revealableCardCampaigns &&
    displayedCardEdition === undefined &&
    revealableCardCampaigns[0]?.blueprintCardEditions[0]
  ) {
    setDisplayedCardEdition(
      revealableCardCampaigns[0]?.blueprintCardEditions[0]
    );
  }

  const selectedCampaignSlug =
    (displayedCardEdition !== undefined &&
      revealableCardCampaigns.find(c =>
        c.blueprintCardEditions.includes(displayedCardEdition)
      )?.slug) ||
    revealableCardCampaigns[0]?.slug;

  const tabs = (
    <>
      <Tab
        to={generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS_TYPE, {
          params: {
            type: defaultCardRarity,
          },
        })}
        matches={[
          catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS)),
          catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_COLLECTIONS)),
          catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_VAULT)),
        ]}
      >
        <FormattedMessage {...galleryTabs.cards} />
      </Tab>
      <Tooltip
        skip={isLaptopAndAbove || unopedBoxCount === 0}
        open
        placement="top"
        title={
          <Horizontal gap={0.5}>
            <LabelM bold>{unopedBoxCount}</LabelM>
            <BoxIcon />
          </Horizontal>
        }
        variant="red"
      >
        <Tab
          to={generateSportContextPath(ANY_SPORT_MY_CARDS_BOXES_TYPE, {
            params: { type: defaultBoxRarity },
          })}
          matches={[
            catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_BOXES)),
          ]}
        >
          <Badge
            invisible={!isLaptopAndAbove || unopedBoxCount === 0}
            badgeContent={unopedBoxCount}
          >
            <FormattedMessage {...fantasy.boxes} />
          </Badge>
        </Tab>
      </Tooltip>
      {!!showEssenceTab && (
        <Tab
          to={generateSportContextPath(ANY_SPORT_MY_CARDS_ESSENCE_TYPE, {
            params: {
              type: defaultEssenceRarity,
            },
          })}
          matches={[
            catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_ESSENCE)),
          ]}
        >
          <FormattedMessage {...fantasy.essenceShort} />
        </Tab>
      )}
    </>
  );

  const matchCollectionsPage = matchAnySportPath(
    catchAll(ANY_SPORT_MY_CARDS_COLLECTIONS),
    location.pathname
  );

  const lastVisitedRarity =
    lifecycle?.[LIFECYCLE.lastVisitedMyCardsRarity]?.[sport];

  const defaultRarity =
    (lastVisitedRarity as Rarity) ||
    (Object.entries(cardsCountByRarity).find(
      ([, value]) => !!value
    )?.[0] as Rarity) ||
    Rarity.limited;

  const updateLastVisitedRarity = (clickedRarity: Rarity) => {
    update(LIFECYCLE.lastVisitedMyCardsRarity, {
      ...(lifecycle?.[LIFECYCLE.lastVisitedMyCardsRarity] || {}),
      [sport]: clickedRarity,
    });
  };

  const rewardsBannerHeight =
    rewardsBannerRef.current?.getBoundingClientRect().height;

  return (
    <>
      <PageHeader text={navLabels.myCards} disableBackBrowser />
      <div ref={rewardsBannerRef}>
        <NewRewardsBanner
          so5Rewards={unclaimedSo5Rewards}
          Wrapper={RewardsBannerWrapper}
        />
      </div>
      <ContentWithStickyHeader
        header={
          <TabBar fullWidth variant="flat">
            {tabs}
          </TabBar>
        }
      >
        <MyCardsLayoutContext.Provider
          value={{
            selectedCampaignSlug,
            defaultRarity,
            rewardsBannerHeight,
          }}
        >
          <Outlet />
        </MyCardsLayoutContext.Provider>
      </ContentWithStickyHeader>
      {!isLaptopAndAbove && (
        <BottomPortal>
          <MaxWidthHorizontal>
            <FullViewportScroll>
              {!!(
                matchAnySportPath(
                  catchAll(ANY_SPORT_MY_CARDS_CARDS),
                  location.pathname
                ) || matchCollectionsPage
              ) &&
                !!type &&
                type !== 'rivals' &&
                type !== 'loyalty' && (
                  <CardOrCollectionRaritySwitcher
                    user={contextCurrentUser}
                    type={type}
                    rarityPath={newRarity => {
                      if (matchCollectionsPage) {
                        return generateSportContextPath(
                          ANY_SPORT_MY_CARDS_COLLECTIONS_TYPE,
                          {
                            params: {
                              type: newRarity,
                            },
                          }
                        );
                      }
                      return generateSportContextPath(
                        ANY_SPORT_MY_CARDS_CARDS_TYPE,
                        {
                          params: {
                            type: newRarity,
                          },
                        }
                      );
                    }}
                    onClick={updateLastVisitedRarity}
                    forceShowCommon={
                      !!matchCollectionsPage &&
                      !!currentUser?.userCardCollectionsPresence
                    }
                  />
                )}
              {matchAnySportPath(
                catchAll(ANY_SPORT_MY_CARDS_BOXES),
                location.pathname
              ) && (
                <BoxRaritySwitcher
                  unopenedProbabilisticBundlesByRarity={
                    unopenedProbabilisticBundlesByRarity
                  }
                  boxTypes={boxTypes}
                  type={type}
                />
              )}
              {matchAnySportPath(
                catchAll(ANY_SPORT_MY_CARDS_ESSENCE),
                location.pathname
              ) && (
                <EssenceRaritySwitcher
                  cardShardsChests={cardShardsChests}
                  rarities={blockchainRarities}
                  type={type}
                  disabled={!!currentUser?.cardPull}
                />
              )}
            </FullViewportScroll>
          </MaxWidthHorizontal>
        </BottomPortal>
      )}
    </>
  );
};

export const UNOPENED_REWARD_BOXES = gql`
  fragment UnopenedRewardBoxes_CurrentUser on CurrentUser {
    slug
    unopenedRewardBoxCommon: unopenedProbabilisticBundles(
      sport: $sport
      first: 0
      rarity: common
      design: PRO
    ) {
      totalCount
    }
    unopenedRewardBoxLimited: unopenedProbabilisticBundles(
      sport: $sport
      first: 0
      rarity: limited
      design: PRO
    ) {
      totalCount
    }
    unopenedRewardBoxLimitedNeonChristmas: unopenedProbabilisticBundles(
      sport: $sport
      first: 0
      rarity: limited
      design: NEON_CHRISTMAS
    ) {
      totalCount
    }
    unopenedRewardBoxRare: unopenedProbabilisticBundles(
      sport: $sport
      first: 0
      rarity: rare
      design: PRO
    ) {
      totalCount
    }
    unopenedRewardBoxSuperRare: unopenedProbabilisticBundles(
      sport: $sport
      first: 0
      rarity: super_rare
      design: PRO
    ) {
      totalCount
    }
    unopenedRewardBoxUnique: unopenedProbabilisticBundles(
      sport: $sport
      first: 0
      rarity: unique
      design: PRO
    ) {
      totalCount
    }
    unopenedRewardBoxRivals: unopenedProbabilisticBundles(
      sport: $sport
      first: 0
      design: RIVALS
    ) {
      totalCount
    }
    unopenedRewardBoxLoyalty: unopenedProbabilisticBundles(
      sport: $sport
      first: 0
      design: LOYALTY
    ) {
      totalCount
    }
  }
` as TypedDocumentNode<UnopenedRewardBoxes_CurrentUser>;

const MY_CARDS_LAYOUT_QUERY = gql`
  query MyCardsLayoutQuery($sport: Sport!) {
    so5 {
      myAvailableFixtureStory(sport: $sport) {
        id
        ...LazyLastFixtureStory_story
      }
    }
    currentUser {
      slug
      cardPull(sport: $sport) {
        id
      }
      userCardCollectionsPresence(rarity: common, sport: $sport)
      unclaimedSo5Rewards(sport: $sport) {
        slug
        ...NewRewardsBanner_so5Reward
      }
      cardShardsChests(sport: $sport) {
        id
        rarity
        cardShardsCount
        newThresholdUnlocked
        ...EssenceRaritySwitcher_cardShardsChest
      }
      cardCollections(startedOnly: true, sport: $sport) {
        raritiesCount {
          rarity
          count
        }
      }
      ...UnopenedRewardBoxes_CurrentUser
    }
  }
  ${NewRewardsBanner.fragments.so5Reward}
  ${UNOPENED_REWARD_BOXES}
  ${LazyLastFixtureStory.fragments.story}
  ${EssenceRaritySwitcher.fragments.cardShardsChest}
` as TypedDocumentNode<MyCardsLayoutQuery, MyCardsLayoutQueryVariables>;

const isNullableOrRarityStr = (
  type: Nullable<string>
): type is Nullable<Exclude<Rarity, typeof Rarity.custom_series>> =>
  !type || (type in Rarity && type !== Rarity.custom_series);

export const MyCardsLayout = <
  Params extends {
    type?: string;
  },
>() => {
  const { type } = useTypedParams<Params>();
  const { generateSportContextPath } = useSportContext();
  const { data } = useQueryLoader<MyCardsLayoutQuery>();

  if (!isNullableOrRarityStr(type) && type !== 'rivals' && type !== 'loyalty') {
    return (
      <Navigate replace to={generateSportContextPath(ANY_SPORT_MY_CARDS)} />
    );
  }
  return (
    <RequireAuth>
      {!!data?.so5?.myAvailableFixtureStory && (
        <LazyLastFixtureStory story={data.so5.myAvailableFixtureStory} />
      )}
      <MyCardsLayoutComponent currentUser={data.currentUser} type={type} />
    </RequireAuth>
  );
};

export const loader = queryLoader(MY_CARDS_LAYOUT_QUERY, ({ sport }) => ({
  sport,
}));
